import { useMutation, useQueryClient } from "@tanstack/react-query";
import { queueApi } from "@bw/shared/api";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * Leave the court queue (DELETE /queue/{entry_id}).
 *
 * Owner-only on the backend; the service atomically rebalances the positions of
 * everyone behind the leaver. On settlement the affected entry and facility
 * state are invalidated so a stale result can't keep the player "in" or "out" of
 * the queue after a rejection. The waiting view only
 * calls this after an explicit confirm in the leave sheet.
 */
export function useLeaveQueue(entryId: string, facilityId?: string) {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, void>({
    mutationFn: () => queueApi.leave(entryId),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dropin.queueEntry(entryId),
        refetchType: "active",
      });
      if (!facilityId) return;
      void queryClient.invalidateQueries({
        queryKey: ["dropin", "my-entry", facilityId],
        refetchType: "active",
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dropin.courtBoard(facilityId),
        refetchType: "active",
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dropin.queueStats(facilityId),
        refetchType: "active",
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dropin.myParty(facilityId),
        refetchType: "active",
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dropin.partyMemberships(facilityId),
        refetchType: "active",
      });
    },
  });
}
